/* ──────────────────────────────────────────────────────────────────────────
 * IPO Navigator — standalone corporate IPO command centre.
 *
 * This is an ISOLATED, SEPARATE BUILD. It deliberately does NOT import any
 * widgets, data or logic from the main Dashboard / IPOLabs application. It is a
 * faithful 1:1 React port of `ipo_platform_dashboard - 4june26.html`.
 *
 * Styles live in the scoped `IPONavigator.css` (every selector under
 * `.ipo-nav-root`). Tabler Icons webfont + Playfair/DM Sans are loaded in
 * public/index.html.
 * ────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import DocumentVaultPanel from "./DocumentVaultPanel";
import { AdvisorMarketplacePanel } from "./AdvisorMarketplace";
import ReadinessAssessmentDesk from "./ReadinessAssessmentDesk";
import ValuationStudioDesk from "./ValuationStudioDesk";
import JourneyPlanner from "./JourneyPlanner";
import AuditLogPanel from "./AuditLogPanel";
import CostTrackerDesk from "./CostTrackerDesk";
import WorkspaceRefBar from "@/components/WorkspaceRefBar";
import { logJourney } from "@/lib/journey";
import OracleKnowledgeBanner from "@/components/OracleKnowledgeBanner";
import "./DocumentVault.css";
import "./AdvisorMarketplace.css";
import "./IPONavigator.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* Derive the user's first name from their saved name, else from their email. */
const deriveFirstName = (u) => {
  if (!u) return "";
  const name = (u.name || "").trim();
  if (name) return name.split(/\s+/)[0];
  const email = (u.email || "").trim();
  if (email.includes("@")) {
    const local = email.split("@")[0];
    const token = local.split(/[._\-0-9]+/).filter(Boolean)[0] || local;
    return token.charAt(0).toUpperCase() + token.slice(1);
  }
  return "";
};

/* Two-letter avatar initials from the user's name (or first name fallback). */
const deriveInitials = (u, firstName) => {
  const name = (u?.name || "").trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
  }
  if (firstName) return firstName.slice(0, 2).toUpperCase();
  return "";
};

const SCREENS = [
  { key: "dashboard", nav: "Dashboard", sw: "Dashboard" },
  { key: "journey", nav: "Journey", sw: "Journey Planner" },
  { key: "readiness", nav: "Readiness Score", sw: "Readiness Score" },
  { key: "advisors", nav: "Advisors", sw: "Advisor Marketplace" },
  { key: "drhp", nav: "My DRHP Workspace", sw: "My DRHP Workspace", external: "/drhp-workspace?phase=DATA_COLLECTION" },
  { key: "docs", nav: "Documents", sw: "Document Vault" },
  { key: "costs", nav: "Cost Tracker", sw: "Cost Tracker" },
];

// Each IPO stage in the top progress bar maps to the View-Screen module(s) it
// drives. Clicking a stage "lights up" (highlights) those module buttons.
const STAGE_HIGHLIGHTS = {
  1: ["readiness"],   // Readiness
  2: ["advisors"],                 // Team Assembly
  3: ["drhp", "docs"],             // DRHP Filing
  4: ["costs"],                    // Cost Analysis
  5: ["journey"],                  // SEBI Filing & Roadshow
};

const IPONavigator = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [screen, setScreen] = useState("dashboard");
  const [highlightedScreens, setHighlightedScreens] = useState([]);
  const [firstName, setFirstName] = useState("");
  const [initials, setInitials] = useState("");

  // Deep-link support: /ipo-navigator?screen=readiness&mode=sme opens a specific
  // in-app screen directly (used by the "Try our free tools" launchers).
  const readinessMode = searchParams.get("mode") === "sme" ? "sme" : "financial";
  useEffect(() => {
    const sp = searchParams.get("screen");
    const allowed = ["dashboard", "journey", "readiness", "valuation", "advisors", "docs", "costs", "audit"];
    if (sp && allowed.includes(sp)) setScreen(sp);
  }, [searchParams]);

  // Pull the logged-in user's first name from their login details to personalise
  // this freshly-created workspace.
  useEffect(() => {
    let active = true;
    axios
      .get(`${API_URL}/api/auth/me`, { withCredentials: true })
      .then((res) => {
        if (!active) return;
        const fn = deriveFirstName(res.data);
        setFirstName(fn);
        setInitials(deriveInitials(res.data, fn));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // Standalone build — chat/AI prompt hooks are intentionally inert here.
  const sendPrompt = () => {};

  // Record each screen visit into the user's journey audit trail.
  useEffect(() => {
    const meta = {
      dashboard: ["IPO Navigator", "Opened dashboard"],
      journey: ["Journey Planner", "Opened Journey Planner"],
      readiness: ["Readiness Score", "Opened Readiness Score"],
      advisors: ["Advisor Marketplace", "Opened Advisor Marketplace"],
      docs: ["Document Vault", "Opened Document Vault"],
      valuation: ["Valuation Studio", "Opened Valuation Studio"],
      audit: ["Audit Log", "Opened Audit Log"],
    }[screen];
    if (meta) logJourney(meta[0], "Viewed", meta[1]);
  }, [screen]);

  const [dash, setDash] = useState(null);

  // Live workspace dashboard — readiness, advisors, issue, activity, gaps.
  // Refetched whenever the user returns to the Dashboard screen so engaging an
  // advisor or editing the DRHP reflects here immediately.
  useEffect(() => {
    if (screen !== "dashboard") return;
    let active = true;
    fetch(`${API_URL}/api/workspace/dashboard`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => { if (active) setDash(d); })
      .catch(() => {});
    return () => { active = false; };
  }, [screen]);

  const k = dash?.kpis || {};
  const health = dash?.health || {};
  const readinessPct = Math.max(0, Math.min(100, Math.round(k.readiness || 0)));
  const ringOffset = (175.9 * (1 - readinessPct / 100)).toFixed(1);
  const stageNames = ["Readiness", "Team Assembly", "DRHP Filing", "Cost Analysis", "SEBI Filing & Roadshow", "Listing"];
  const stage = dash?.stage || 1;
  const tagClass = (t) => ({ Setup: "tag-legal", Finance: "tag-finance", Team: "tag-banker", Issue: "tag-sebi", SEBI: "tag-sebi" }[t] || "tag-finance");
  const statusClass = (s) => ({ engaged: "status-active", filled: "status-active", shortlisted: "status-pending", open: "status-search" }[s] || "status-search");
  const advAvatarColors = [["#E6F1FB", "#0C447C"], ["#E1F5EE", "#085041"], ["#EEEDFE", "#3C3489"], ["#FAEEDA", "#633806"]];
  const initialsOf = (s) => (s || "").split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "—";
  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }); } catch { return ""; } };

  return (
    <div className="ipo-nav-root" data-testid="ipo-navigator-page">
      <h2 className="sr-only">
        IPO Navigator — India&apos;s first corporate IPO management platform. Shows dashboard,
        journey tracker, advisor marketplace, readiness score and document vault screens.
      </h2>

      {/* Top navigation */}
      <div className="nav">
        <div className="nav-left">
          <div
            className="nav-brand"
            onClick={() => navigate("/dashboard-new")}
            data-testid="ipo-nav-brand"
            title="Back to Dashboard"
          >
            IPO<span>Navigator</span>
          </div>
          <button className="nav-topbtn" onClick={() => navigate("/home")} data-testid="ipo-nav-home"><i className="ti ti-home" aria-hidden="true" /> Home</button>
          <button className="nav-topbtn" onClick={() => navigate("/oracle")} data-testid="ipo-nav-ask-oracle"><i className="ti ti-robot" aria-hidden="true" /> Ask Oracle {"{IPO.GPT}"}</button>
        </div>
        <div className="nav-right">
          <i className="ti ti-bell" style={{ fontSize: 18, color: "var(--color-text-secondary)" }} aria-hidden="true" />
          <div className="avatar" data-testid="ipo-nav-avatar">{initials || "—"}</div>
        </div>
      </div>

      {/* Stage progress bar */}
      <div className="stage-bar" data-testid="ipo-nav-stage-bar">
        {stageNames.map((name, i) => {
          const n = i + 1;
          const state = n < stage ? "done" : n === stage ? "active" : "future";
          const highlights = STAGE_HIGHLIGHTS[n];
          const clickable = !!highlights;
          const isSelected = clickable && highlights.join() === highlightedScreens.join() && highlightedScreens.length > 0;
          return (
            <Fragment key={name}>
              {i > 0 && <div className={`stage-line ${n <= stage ? "done" : ""}`}></div>}
              <div
                className={`stage-item ${clickable ? "clickable" : ""} ${isSelected ? "selected" : ""}`}
                onClick={clickable ? () => setHighlightedScreens(highlights) : undefined}
                data-testid={`ipo-stage-${n}`}
              >
                <div className={`stage-dot ${state}`}>
                  {state === "done" ? <i className="ti ti-check" style={{ fontSize: 11 }} /> : n}
                </div>
                <div className="stage-text">
                  <div className={`stage-name ${state === "future" ? "future" : ""}`} style={state === "active" ? { color: "#185FA5" } : undefined}>{name}</div>
                </div>
              </div>
            </Fragment>
          );
        })}
      </div>

      {/* Screen switcher */}
      <div
        style={{
          display: "flex", gap: 8, padding: "10px 20px",
          background: "var(--color-background-secondary)",
          borderBottom: "0.5px solid var(--color-border-tertiary)",
          flexWrap: "wrap",
        }}
      >
        <div className="screen-label" style={{ padding: 0, margin: 0, alignSelf: "center" }}>View screen:</div>
        {SCREENS.map((s) => {
          const href = s.external || `/ipo-navigator?screen=${s.key}`;
          return (
            <a
              key={s.key}
              href={href}
              className={`sw-btn ${screen === s.key ? "active" : ""} ${highlightedScreens.includes(s.key) ? "highlight" : ""}`}
              onClick={(e) => {
                if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                e.preventDefault();
                if (s.external) navigate(s.external);
                else setScreen(s.key);
              }}
              data-testid={`ipo-swbtn-${s.key}`}
            >
              {s.sw}
            </a>
          );
        })}
      </div>

      {/* Uniform workspace reference bar — entity · user · board · filing date */}
      <WorkspaceRefBar />

      {/* ─── DASHBOARD ─── */}
      {screen === "dashboard" && (
        <div id="screen-dashboard">
          <div className="main-grid">
            {/* Module sidebar */}
            <div className="sidebar">
              <div className="sb-section">
                <div className="sb-label">Overview</div>
                <div className="sb-item active"><i className="ti ti-layout-dashboard" /> Dashboard</div>
                <a className="sb-item" href="/ipo-navigator?screen=journey" onClick={(e) => { if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; e.preventDefault(); setScreen("journey"); }} data-testid="sb-ipo-journey"><i className="ti ti-road" /> IPO Journey <span className="sb-badge">{stage}/6</span></a>
                <a className="sb-item" href="/ipo-navigator?screen=readiness" onClick={(e) => { if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; e.preventDefault(); setScreen("readiness"); }} data-testid="sb-readiness-score"><i className="ti ti-file-check" /> Readiness Score</a>
              </div>
              <div className="sb-section">
                <div className="sb-label">Capital</div>
                <a className="sb-item" href="/ipo-navigator?screen=costs" onClick={(e) => { if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; e.preventDefault(); setScreen("costs"); }} data-testid="sb-cost-tracker"><i className="ti ti-wallet" /> Cost &amp; Payment Tracker</a>
              </div>
              <div className="sb-section">
                <div className="sb-label">Activity</div>
                <a className="sb-item" href="/ipo-navigator?screen=audit" onClick={(e) => { if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; e.preventDefault(); setScreen("audit"); }} data-testid="sb-audit-log"><i className="ti ti-history" /> Audit Log</a>
              </div>
            </div>

            {/* Main content */}
            <div className="content">
              {(dash?.actions || []).length > 0 && (
                <div className="alert-banner" data-testid="ipo-nav-alert">
                  <i className="ti ti-alert-triangle" style={{ fontSize: 16, color: "#BA7517" }} aria-hidden="true" />
                  <div className="alert-text">{dash.actions[0].text}</div>
                  <div className="alert-action" onClick={() => navigate("/drhp-workspace")} style={{ cursor: "pointer" }}>Resolve →</div>
                </div>
              )}

              <div className="page-heading" data-testid="ipo-nav-greeting">Good morning{firstName ? `, ${firstName}` : ""}</div>
              <div className="page-sub">{dash?.companyName ? `${dash.companyName} · ` : ""}{(dash?.actions?.length || 0) > 0 ? `${dash.actions.length} action${dash.actions.length > 1 ? "s" : ""} need your attention.` : (dash ? "You're on track — no open actions right now." : "Loading your workspace…")}</div>

              <div className="kpi-grid">
                <div className="kpi-card">
                  <div className="kpi-label">IPO Readiness</div>
                  <div className="kpi-val kpi-info" data-testid="kpi-readiness">{readinessPct}%</div>
                  <div className="kpi-sub">DRHP · advisors · documents</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">Target Issue Size</div>
                  <div className="kpi-val" data-testid="kpi-issue-size">{k.issueSize || "—"}</div>
                  <div className="kpi-sub">{dash?.listingType === "sme" ? "SME · BSE/NSE Emerge" : dash?.listingType === "mb" ? "Main Board · NSE/BSE" : "From DRHP issue details"}</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">Advisors Onboarded</div>
                  <div className="kpi-val kpi-good" data-testid="kpi-advisors">{k.advisorsEngaged ?? 0} / {k.advisorsTotal ?? 0}</div>
                  <div className="kpi-sub">{Math.max((k.advisorsTotal ?? 0) - (k.advisorsEngaged ?? 0), 0)} roles open</div>
                </div>
                <div className="kpi-card">
                  <div className="kpi-label">Master Data</div>
                  <div className="kpi-val kpi-warn" data-testid="kpi-data">{k.dataCompleteness ?? 0}%</div>
                  <div className="kpi-sub">Complete in DRHP Workspace</div>
                </div>
              </div>

              <div className="two-col">
                <div className="card">
                  <div className="card-title">Today&apos;s Action Items <span className="card-title-action" onClick={() => navigate("/drhp-workspace")} style={{ cursor: "pointer" }}>Open DRHP</span></div>
                  {(dash?.actions || []).length === 0 ? (
                    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", padding: "10px 0" }}>
                      {dash ? "No open action items — your workspace is on track." : "Loading…"}
                    </div>
                  ) : (
                    dash.actions.map((a, i) => (
                      <div className="task-item" key={i} data-testid={`ipo-action-${i}`}>
                        <div className="task-check"></div>
                        <div style={{ flex: 1 }}>
                          <div className="task-text">{a.text}</div>
                          <div style={{ marginTop: 4 }}><span className={`task-tag ${tagClass(a.tag)}`}>{a.tag}</span></div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="card">
                  <div className="card-title">Advisor Team <span className="card-title-action" onClick={() => setScreen("advisors")} style={{ cursor: "pointer" }}>Manage</span></div>
                  {(dash?.advisorTeam || []).length === 0 ? (
                    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", padding: "10px 0" }}>
                      No advisors engaged yet. <span style={{ color: "#185FA5", cursor: "pointer" }} onClick={() => setScreen("advisors")}>Browse the Advisor Marketplace →</span>
                    </div>
                  ) : (
                    dash.advisorTeam.map((a, i) => {
                      const [bg, color] = advAvatarColors[i % advAvatarColors.length];
                      return (
                        <div className="advisor-item" key={i} data-testid={`ipo-advisor-${i}`}>
                          <div className="adv-avatar" style={{ background: bg, color }}>{initialsOf(a.role)}</div>
                          <div><div className="adv-name">{a.role}</div><div className="adv-role">DRHP team role</div></div>
                          <div className={`adv-status ${statusClass(a.status)}`}>{a.statusLabel}</div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              <OracleKnowledgeBanner className="ipo-nav-oracle" />
            </div>

            {/* Right panel */}
            <div className="right-panel">
              <div className="rp-title">IPO Health Score</div>
              <div style={{ textAlign: "center", marginBottom: 16 }}>
                <svg className="readiness-ring" viewBox="0 0 70 70" style={{ display: "block" }}>
                  <circle cx="35" cy="35" r="28" fill="none" stroke="var(--color-border-tertiary)" strokeWidth="6" />
                  <circle cx="35" cy="35" r="28" fill="none" stroke="#1D9E75" strokeWidth="6" strokeDasharray="175.9 175.9" strokeDashoffset={ringOffset} strokeLinecap="round" transform="rotate(-90 35 35)" />
                  <text x="35" y="38" textAnchor="middle" fontSize="14" fontWeight="500" fill="var(--color-text-primary)" fontFamily="DM Sans,sans-serif">{readinessPct}%</text>
                </svg>
                <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>Overall readiness</div>
              </div>

              <div className="health-item">
                <div className="health-label">Financials</div>
                <div className="health-bar-wrap"><div className="health-bar" style={{ width: `${health.financials || 0}%`, background: "#1D9E75" }} /></div>
                <div className="health-val kpi-good">{health.financials || 0}%</div>
              </div>
              <div className="health-item">
                <div className="health-label">Compliance</div>
                <div className="health-bar-wrap"><div className="health-bar" style={{ width: `${health.compliance || 0}%`, background: "#185FA5" }} /></div>
                <div className="health-val kpi-info">{health.compliance || 0}%</div>
              </div>
              <div className="health-item">
                <div className="health-label">Governance</div>
                <div className="health-bar-wrap"><div className="health-bar" style={{ width: `${health.governance || 0}%`, background: "#BA7517" }} /></div>
                <div className="health-val kpi-warn">{health.governance || 0}%</div>
              </div>
              <div className="health-item">
                <div className="health-label">Team</div>
                <div className="health-bar-wrap"><div className="health-bar" style={{ width: `${health.team || 0}%`, background: "#D85A30" }} /></div>
                <div className="health-val" style={{ color: "#993C1D" }}>{health.team || 0}%</div>
              </div>
              <div className="health-item">
                <div className="health-label">Documents</div>
                <div className="health-bar-wrap"><div className="health-bar" style={{ width: `${health.documents || 0}%`, background: "#1D9E75" }} /></div>
                <div className="health-val kpi-good">{health.documents || 0}%</div>
              </div>
              <div className="health-item">
                <div className="health-label">DRHP Drafting</div>
                <div className="health-bar-wrap"><div className="health-bar" style={{ width: `${health.drhp || 0}%`, background: "#6C4BF4" }} /></div>
                <div className="health-val" style={{ color: "#5b3fd1" }} data-testid="health-drhp">{health.drhp || 0}%</div>
              </div>

              <div style={{ borderTop: "0.5px solid var(--color-border-tertiary)", margin: "14px 0" }}></div>
              <div className="rp-title">Recent Activity</div>
              {(dash?.activity || []).length === 0 ? (
                <div style={{ fontSize: 11.5, color: "var(--color-text-secondary)" }}>No recent activity yet. Edits in your DRHP Workspace will appear here.</div>
              ) : (
                dash.activity.map((ev, i) => (
                  <div className="timeline-item" key={i} data-testid={`ipo-activity-${i}`}>
                    <div className="tl-dot-wrap"><div className="tl-dot" style={{ background: i === 0 ? "#1D9E75" : i === 1 ? "#185FA5" : "#BA7517" }} />{i < dash.activity.length - 1 && <div className="tl-line"></div>}</div>
                    <div><div className="tl-date">{fmtDate(ev.date)}{ev.by ? ` · ${ev.by}` : ""}</div><div className="tl-text">{ev.text}</div></div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── JOURNEY PLANNER ─── */}
      {screen === "journey" && (
        <div id="screen-journey">
          <div style={{ padding: 20 }}>
            <div className="page-heading">IPO Journey Planner</div>
            <div className="page-sub">Six expandable stages from readiness to listing — set your Target Filing Date and the timelines auto-rescale. Tap any step to jump straight to its module.</div>
            <div style={{ marginTop: 16 }}>
              <JourneyPlanner setScreen={setScreen} />
            </div>
          </div>
        </div>
      )}

      {/* ─── READINESS SCORE ─── */}
      {screen === "readiness" && (
        <div id="screen-readiness">
          <div style={{ padding: 20 }}>
            <div className="page-heading">IPO Readiness Score</div>
            <div className="page-sub">Run the Pre-IPO Financial Assessment against SEBI eligibility, valuation and governance benchmarks — or self-check your eligibility for the BSE SME / NSE Emerge boards. Download a board-ready PDF report at the end.</div>
            <ReadinessAssessmentDesk initialMode={readinessMode} />
          </div>
        </div>
      )}

      {/* ─── ADVISOR MARKETPLACE ─── */}
      {screen === "advisors" && (
        <div id="screen-advisors">
          <div style={{ padding: 20 }}>
            <div className="page-heading">Advisor Marketplace</div>
            <div className="page-sub">Discover, compare and onboard SEBI-registered advisors, BRLMs, auditors and legal experts — verified for IPO mandates.</div>
            <div className="adv-mp-root" data-testid="ipo-nav-advisor-marketplace" style={{ background: "transparent", minHeight: "auto", marginTop: 18 }}>
              <AdvisorMarketplacePanel embedded />
            </div>
          </div>
        </div>
      )}

      {/* ─── DOCUMENT VAULT ─── */}
      {screen === "docs" && (
        <div id="screen-docs">
          <div style={{ padding: 20 }}>
            <div className="page-heading">Document Vault</div>
            <div className="page-sub">Centralised repository of all SEBI-mandated documents. Track status, upload and share with advisors securely.</div>
            <div className="doc-vault-root" data-testid="ipo-nav-document-vault" style={{ background: "transparent", minHeight: "auto", marginTop: 14 }}>
              <DocumentVaultPanel embedded />
            </div>
          </div>
        </div>
      )}

      {/* ─── VALUATION STUDIO ─── */}
      {screen === "valuation" && (
        <div id="screen-valuation">
          <div style={{ padding: 20 }}>
            <div className="page-heading">Valuation Studio</div>
            <div className="page-sub">Run a full Business Valuation — DCF, NAV and Comparable Company methods in parallel, with sector-aware listed peers. Create up to 3 BV projects.</div>
            <ValuationStudioDesk />
          </div>
        </div>
      )}

      {/* ─── COST & PAYMENT TRACKER ─── */}
      {screen === "costs" && (
        <div id="screen-costs">
          <div style={{ padding: 20 }}>
            <div className="page-heading">Cost &amp; Payment Tracker</div>
            <div className="page-sub">Track every IPO cost across all channels — professional fees, regulatory, depository, marketing, capital and post-listing. Add the parties you onboard, mark who paid, and export the ledger to Excel or PDF. Advisor fees sync from the Marketplace.</div>
            <div style={{ marginTop: 16 }}>
              <CostTrackerDesk />
            </div>
          </div>
        </div>
      )}

      {/* ─── AUDIT LOG ─── */}
      {screen === "audit" && (
        <div id="screen-audit">
          <div style={{ padding: 20 }}>
            <div className="page-heading">Audit Log</div>
            <div className="page-sub">A complete, time-stamped record of your IPO journey — every step taken across your workspace, who did it and when. Generated automatically for your account.</div>
            <div style={{ marginTop: 16 }}>
              <AuditLogPanel />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Small presentational helpers (local to this isolated build) ── */
const ValBar = ({ label, width, color, num, numColor }) => (
  <div className="valuation-bar">
    <div className="val-label">{label}</div>
    <div className="val-bar-wrap"><div className="val-bar" style={{ width, background: color }} /></div>
    <div className="val-num" style={numColor ? { color: numColor } : undefined}>{num}</div>
  </div>
);

const ModelStat = ({ label, value, color }) => (
  <div style={{ background: "var(--color-background-secondary)", borderRadius: "var(--border-radius-md)", padding: 12 }}>
    <div style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{label}</div>
    <div style={{ fontSize: 20, fontWeight: 500, ...(color ? { color } : {}) }}>{value}</div>
  </div>
);

export default IPONavigator;
